/**
 * @lines98/client/lib/board-appearance — ЕДИНЫЙ ствол применения ОФОРМЛЕНИЯ к доске
 * (палитра × материал). appearance-skins.md §3.1/§7.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (модель «ствол → ветки», owner-directive 2026-05-30):
 *   Применение оформления к доске нужно В ДВУХ местах — (1) превью-свотчам в галерее
 *   (`lib/swatch-gallery`), (2) боевым доскам/реплеям (`board-screen`, `replay-screen`,
 *   `result-screen`, `daily-board-screen`). Раньше set-логику палитры дублировал
 *   `skin-gallery.ts` (накладывал только на превью). Этот модуль — ОДИН источник:
 *   обе поверхности зовут его, не копируя set-переменных (AC-9).
 *
 * КАК (§3, ортогональность I-App-2):
 *   - Палитра (ось «Доска») = карта CSS-переменных ТОЛЬКО фона/клеток (`--board-bg*`,
 *     `--cell`) — owner-directive 2026-06-16: ось «Доска» меняет фон/клетки и НИКОГДА
 *     цвет шаров. Выставляется set'ом `el.style.setProperty(...)` на хост-обёртке доски.
 *     Цвета шаров — фиксированные `--ball-1..7` (global.css), палитра их не трогает;
 *     шары читают цвет через `.bcN { --bc: var(--ball-N) }` (board.css).
 *   - Материал (ось «Шары») = `data-ball-style="classic|glass|pearl"` на том же хосте
 *     → board.css по нему выбирает рецепт блика (читает уже-установленный `--bc` +
 *     производные `--lit/--glow` через color-mix). Цвета материал НЕ задаёт.
 *
 * ПОЧЕМУ ХОСТ, А НЕ `:root` (§7, рекомендация спеки): на одном экране могут жить
 *   РАЗНЫЕ доски одновременно (галерея превью × N) с разными палитрами/материалами →
 *   переменные вешаем на хост-обёртку каждой доски, не на глобальный `:root`.
 *
 * SCOPE: чистое применение (set vars + attr) + реактивная привязка боевой доски к
 *   `client-prefs` (subscribePrefs). NOT IN SCOPE: рендер сетки/шаров (это board-view);
 *   данные реестра (skin-registry); сетка свотчей (swatch-gallery).
 *
 * Cross-references: appearance-skins.md §3/§7; `lib/skin-registry`; `lib/client-prefs`;
 * `lib/board-view`; `styles/board.css` (рецепты материалов).
 */

import {
  getBallStyle,
  getPalette,
  type BallStyleDef,
  type PaletteDef,
} from './skin-registry.js';
import { getClientPrefs, subscribePrefs } from './client-prefs.js';

/** CSS-переменные палитры, которые сбрасываем перед наложением новой (чтобы смена
 *  палитры не оставляла «хвостов» прошлой на том же хосте). Полный union ключей по
 *  всем палитрам реестра — берём из дефолт-набора имён (single source). */
const PALETTE_VARS = [
  '--board-bg',
  '--board-bg-2',
  '--cell',
  '--ball-1',
  '--ball-2',
  '--ball-3',
  '--ball-4',
  '--ball-5',
  '--ball-6',
  '--ball-7',
] as const;

/** Атрибут материала на хосте доски (читается board.css). */
const BALL_STYLE_ATTR = 'data-ball-style';

export interface AppearanceSelection {
  /** Id палитры (ось «Доска»). Дефолт/невалид → standard (getPalette fallback). */
  readonly paletteId: string;
  /** Id материала (ось «Шары»). Дефолт/невалид → classic (getBallStyle fallback). */
  readonly ballStyleId: string;
}

/**
 * Применить оформление к ОДНОМУ хосту доски: наложить переменные палитры + выставить
 * атрибут материала. Идемпотентно (сначала чистит прошлые палитра-переменные).
 * Зовётся и превью-свотчем (со своей фикс-комбинацией), и боевой доской (с prefs).
 */
export function applyAppearance(host: HTMLElement, sel: AppearanceSelection): void {
  const palette: PaletteDef = getPalette(sel.paletteId);
  const ballStyle: BallStyleDef = getBallStyle(sel.ballStyleId);

  // Палитра: сброс прошлых ключей → наложение новых. Дефолт (standard) = пустая
  // карта → ничего не вешаем, доска наследует токены global.css (§3.1).
  for (const k of PALETTE_VARS) host.style.removeProperty(k);
  if (palette.palette) {
    for (const [k, v] of Object.entries(palette.palette)) host.style.setProperty(k, v);
  }

  // Материал: атрибут (classic — тоже выставляем явно, чтобы смена pearl→classic
  // вернула дефолтный рецепт; board.css трактует отсутствие атрибута == classic).
  host.setAttribute(BALL_STYLE_ATTR, ballStyle.id);
}

/** Текущая комбинация из client-prefs (boardSkin = палитра, ballStyle = материал). */
export function currentAppearance(): AppearanceSelection {
  const p = getClientPrefs();
  return { paletteId: p.boardSkin, ballStyleId: p.ballStyle };
}

export interface LiveAppearanceHandle {
  /** Снять подписку на prefs (на размонтировании экрана-доски). */
  detach(): void;
}

/**
 * Привязать БОЕВУЮ доску к client-prefs реактивно (AC-2/AC-3): применяет текущую
 * комбинацию сразу + перекрашивает хост на каждую смену оформления в сессии БЕЗ
 * пере-рендера сетки (меняются только CSS-переменные/атрибут — §2.2). Зовётся
 * экранами живой игры / реплея / результата / превью доски дня поверх их
 * `board-view.frame` (или его обёртки).
 */
export function attachLiveAppearance(host: HTMLElement): LiveAppearanceHandle {
  applyAppearance(host, currentAppearance());
  const unsub = subscribePrefs(() => applyAppearance(host, currentAppearance()));
  return { detach: unsub };
}
